import { useEffect, useState } from 'react';
import { generateFaviconFromImage } from '../utils/faviconGenerator';

let defaultFaviconHref: string | null = null;
let currentFaviconHref: string | null = null;

/**
 * Route detection utilities for favicon management
 */
export const isPublicAuthRoute = (pathname: string): boolean => {
  const publicRoutes = ['/', '/login', '/signup', '/register', '/forgot-password', '/reset-password'];
  return publicRoutes.includes(pathname);
};

export const isPublicProfileRoute = (pathname: string): boolean => {
  return /^\/(connect|user|u)\//.test(pathname);
};

export const isAuthenticatedRoute = (pathname: string): boolean => {
  return pathname.startsWith('/dashboard');
};

const ensureFaviconLink = (): HTMLLinkElement | null => {
  if (typeof document === 'undefined') return null;

  const existing = document.querySelector<HTMLLinkElement>("link[rel='icon']")
    || document.querySelector<HTMLLinkElement>("link[rel='shortcut icon']");

  if (existing) {
    return existing;
  }

  const link = document.createElement('link');
  link.rel = 'icon';
  document.head.appendChild(link);
  return link;
};

/**
 * Converts a relative URL to an absolute URL
 */
const toAbsoluteUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Convert relative URL to absolute
  if (url.startsWith('/')) {
    return `${window.location.origin}${url}`;
  }
  return `${window.location.origin}/${url}`;
};

/**
 * Gets the default favicon URL (always default-avatar, never vite.svg)
 * Supports both .svg and .png extensions
 */
const getDefaultFaviconUrl = (): string => {
  // Try PNG first, fallback to SVG (both work as favicons)
  return `${window.location.origin}/default-avatar.svg`;
};

const restoreDefaultFavicon = (link: HTMLLinkElement) => {
  // Always restore to default-avatar, never vite.svg
  const defaultUrl = getDefaultFaviconUrl();
  link.href = defaultUrl;
  link.removeAttribute('type');
  currentFaviconHref = defaultUrl;
};

interface FaviconOptions {
  fallbackHref?: string | null;
  persist?: boolean;
  forceDefault?: boolean;
}

const useDynamicFavicon = (imageUrl?: string | null, options?: FaviconOptions) => {
  const fallbackHref = options?.fallbackHref ?? null;
  const persist = options?.persist ?? false;
  const forceDefault = options?.forceDefault ?? false;
  const [generatedFavicon, setGeneratedFavicon] = useState<string | null>(null);

  // Initialize default favicon on first load
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const link = ensureFaviconLink();
    if (!link) return;

    if (defaultFaviconHref === null) {
      // Store the initial favicon, but we'll override it to use default-avatar.svg
      defaultFaviconHref = link.getAttribute('href');
      
      // If it's vite.svg, replace it with default-avatar.svg
      if (defaultFaviconHref === '/vite.svg' || defaultFaviconHref?.includes('vite.svg')) {
        const defaultUrl = getDefaultFaviconUrl();
        link.href = defaultUrl;
        defaultFaviconHref = defaultUrl;
        currentFaviconHref = defaultUrl;
      }
    }
  }, []);

  // Generate favicon from image URL (skip if forceDefault is true)
  useEffect(() => {
    if (forceDefault || !imageUrl) {
      setGeneratedFavicon(null);
      return;
    }

    // Ensure imageUrl is absolute
    const absoluteImageUrl = imageUrl.startsWith('http') ? imageUrl : toAbsoluteUrl(imageUrl);
    
    generateFaviconFromImage(absoluteImageUrl)
      .then((faviconDataUrl) => {
        setGeneratedFavicon(faviconDataUrl);
      })
      .catch((error) => {
        console.error('Failed to generate favicon:', error);
        setGeneratedFavicon(null);
      });
  }, [imageUrl, forceDefault]);

  // Update favicon link element
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const link = ensureFaviconLink();
    if (!link) return;

    let targetHref: string | null = null;

    // If forceDefault is true, always use default avatar
    if (forceDefault) {
      targetHref = getDefaultFaviconUrl();
    } else {
      // Priority: generated favicon > fallback > default
      if (generatedFavicon) {
        targetHref = generatedFavicon;
      } else if (fallbackHref) {
        // Convert fallback to absolute URL if relative
        targetHref = fallbackHref.startsWith('http') 
          ? fallbackHref 
          : toAbsoluteUrl(fallbackHref);
      } else {
        // Use default-avatar.svg as default
        targetHref = getDefaultFaviconUrl();
      }
    }

    if (!targetHref) {
      restoreDefaultFavicon(link);
      return;
    }

    // Prevent vite.svg from ever being set
    if (targetHref.includes('vite.svg')) {
      targetHref = getDefaultFaviconUrl();
    }

    // Only update if the href has changed
    if (currentFaviconHref !== targetHref) {
      link.href = targetHref;
      link.removeAttribute('type');
      currentFaviconHref = targetHref;
    }

    // Cleanup for non-persistent mode
    if (!persist) {
      return () => {
        if (currentFaviconHref === targetHref) {
          restoreDefaultFavicon(link);
        }
      };
    }
  }, [generatedFavicon, fallbackHref, persist, forceDefault]);
};

export default useDynamicFavicon;

